import React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { v4 } from 'uuid';
import {
  ADD_MODULE_PAGE,
  ExtensionType,
  ModuleSettingsItem,
  MODULE_MANAGEMENT_PAGE,
} from '../../../../constants';
import { RouteContext } from '../../../../context';
import history from '../../../../history';
import { useEditModuleForm } from '../../../../hooks/useEditModuleV2';
import { usePortalModulesState } from '../../../../hooks/usePortalModulesState';
import { RootState } from '../../../../store';
import {
  updateExtensionSettingsAction,
  updateModuleSettings,
} from '../../../../store/settings/actions';
import { ExtensionViewTypeValue } from '../../ExtensionsPage/ExtensionViewType';
import { SettingsPageForm } from '../../SettingsPageForm';
import {
  ModuleFormValuesType,
  ModuleTypes,
  SetupModulePageQueryParams,
} from './moduleManagementTypes';
import SetupModuleBreadCrumbs from './SetupModuleBreadCrumbs';
import { useEditingModule } from './useEditingModule';
import SetupModulePageDescription from './SetupModulePageDescription';
import { ModuleContainer } from '../../ModuleContainer';

const initialValues: ModuleFormValuesType = {
  id: '',
  label: '',
  icon: 'dashboard',
  type: ModuleTypes.Global, // for extension module
  embedLink: '', // for global extensions connected as link
  viewType: ExtensionViewTypeValue.EMBED,
  autoSizeEmbed: true,
};

const SetupModulePage: React.FC = () => {
  const { query } = React.useContext(RouteContext);
  const { moduleSettings, extensionsSettings } = useSelector(
    (state: RootState) => ({
      moduleSettings: state.settings.moduleSettings,
      extensionsSettings: state.settings.extensionsSettings,
    }),
    shallowEqual,
  );
  const [editingModule, setEditingModule] = React.useState<
    ModuleFormValuesType
  >(initialValues);

  const { moduleId, moduleType } = query as SetupModulePageQueryParams;
  const isCoreModule = moduleType === 'module';
  const currentEditingModule = useEditingModule();
  const dispatch = useDispatch();
  const portalModules = usePortalModulesState();

  /**
   * This hook initialize the editing module.
   * It should check for module id to denote if it is the edit module
   * flow or add module flow.
   * In edit module flow, editing module state holds the updating module
   * In add module flow, editing module state holds the initial add module form value.
   */
  React.useEffect(() => {
    if (moduleId !== 'new') {
      if (currentEditingModule) {
        setEditingModule(currentEditingModule);
      }
    } else {
      // add custom module flow
      setEditingModule({
        id: '',
        type: ModuleTypes.Global,
        icon: 'dashboard',
        label: '',
        embedCode: '',
        autoSizeEmbed: true, // for global extension, auto-size embed check should be initially true
        embedLink: '',
        viewType: ExtensionViewTypeValue.EMBED,
      });
    }
  }, [moduleId, moduleSettings, extensionsSettings]);

  /**
   * This function calculates the updating core module disabled state based
   * If it is disabled and it is the editing module, it should be enabled
   * (scenario: enabling a core module).
   * Otherwise, keep its disabled state (do not change)
   * @param module
   * @returns
   */
  const getUpdatingModuleDisabledStatus = (module: ModuleSettingsItem) => {
    if (moduleType === 'module' && module.disabled) {
      return false;
    }

    return module.disabled;
  };

  /**
   * When an extension is an authenticated extension type
   * set the type as 'global' in extension config
   * in the future we may want to also allow local extensions to be authenticated
   * @param updatedModule the updated extension module
   * @returns { type: 'global' | 'local', isAuthenticated: boolean }
   */
  const getAuthenticatedExtensionProperties = (
    updatedModule: ModuleFormValuesType,
  ) => {
    let extensionType = updatedModule.type;
    const isAuthenticated = extensionType === ModuleTypes.Authenticated;
    if (isAuthenticated) {
      extensionType = ModuleTypes.Global;
    }
    return { isAuthenticated, type: extensionType as ExtensionType };
  };

  /**
   *
   * @param updatedModule the updated extension module
   * handles both add and update scenarios.
   * The function updates both extensions and moduleSettings in redux store.
   * After successful update, it redirects to the module management page.
   */
  const handleSave = async (updatedModule: ModuleFormValuesType) => {
    //  Formatting the initialModulesState (defined as portalModules) to ModuleSettingsItem
    //  array structure to be sent as updateModulesSettings action payload.
    const stagedAvailableModules = portalModules.map(module => ({
      id: module.id,
      label: module.title,
      type: module.type,
      icon: module.iconName,
      disabled:
        moduleSettings &&
        moduleSettings.find(m => m.id === module.id)?.disabled,
    }));

    // edit module flow
    if (moduleId !== 'new') {
      const updatedModuleSettings = stagedAvailableModules.map(module => {
        if (module.id === editingModule.id) {
          return {
            id: module.id,
            icon: updatedModule.icon,
            label: updatedModule.label,
            disabled: getUpdatingModuleDisabledStatus(
              module as ModuleSettingsItem,
            ),
            type: module.type,
          };
        }

        return module;
      });

      dispatch(updateModuleSettings(updatedModuleSettings));
      // if module is extension, update its extension config
      if (!isCoreModule) {
        const updatingExtensionConfig = extensionsSettings
          ? extensionsSettings[editingModule.id]
          : null;

        if (updatingExtensionConfig) {
          const updatedExtensionSettings = {
            ...extensionsSettings,
            [editingModule.id]: {
              ...updatingExtensionConfig,
              ...getAuthenticatedExtensionProperties(updatedModule),
              name: updatedModule.label,
              embedCode: updatedModule.embedCode,
              embedLink: updatedModule.embedLink,
              autoSizeEmbed: updatedModule.autoSizeEmbed,
              embedlyData: updatedModule.embedlyData,
              icon: updatedModule.icon,
            },
          };
          await dispatch(
            updateExtensionSettingsAction(updatedExtensionSettings),
          );
        }
      }
      history.push(MODULE_MANAGEMENT_PAGE.path);
      return;
    }

    // add new extension flow
    const newExtensionModuleConfigId: string = v4();
    const newExtensionModule = {
      ...getAuthenticatedExtensionProperties(updatedModule),
      icon: updatedModule.icon,
      name: updatedModule.label,
      embedCode: updatedModule.embedCode,
      embedLink: updatedModule.embedLink,
      autoSizeEmbed: updatedModule.autoSizeEmbed,
      embedlyData: updatedModule.embedlyData,
    };

    const moduleSettingsItem = {
      id: newExtensionModuleConfigId,
      icon: updatedModule.icon,
      disabled: false,
      ...getAuthenticatedExtensionProperties(updatedModule),
    };

    const updatedExtensionSettings = {
      ...extensionsSettings,
      [newExtensionModuleConfigId]: newExtensionModule,
    };
    await dispatch(updateExtensionSettingsAction(updatedExtensionSettings));
    // making new extension item part of the module settings for optimistic update
    await dispatch(
      updateModuleSettings([...stagedAvailableModules, moduleSettingsItem]),
    );
    const extensionRedirectionURL = `/extensions?id=${newExtensionModuleConfigId}`;
    history.push(extensionRedirectionURL);
  };

  /**
   * This function calculates the form action button label
   * For core modules, when it is disabled it should be, the form action button
   * label should be 'Enable', otherwise it should be 'Save'.
   * For extension module, when it is the add flow (moduleId='new')
   * it should be 'Add', otherwise on edit extension flow it should be 'Save'
   */
  const getFormActionLabel = React.useCallback(() => {
    if (isCoreModule) {
      return editingModule.disabled ? 'Enable' : 'Save';
    }
    return moduleId === 'new' ? 'Add' : 'Save';
  }, [moduleType, moduleId, editingModule]);

  /**
   * This function handle the cancel action when it is an add module flow.
   * if it is an add/enable module flow, it should redirect to module management page.
   */
  const handleCancel = () => {
    // cancel add extension or enable core module should redirect to add module page
    if (moduleId === 'new' || editingModule.disabled) {
      history.push(ADD_MODULE_PAGE.path);
    }
  };

  return (
    <ModuleContainer description={<SetupModulePageDescription />}>
      <SettingsPageForm
        BreadcrumbsComponent={<SetupModuleBreadCrumbs />}
        initialFormValue={editingModule}
        useFormHook={useEditModuleForm}
        handleSave={handleSave}
        actionLabel={getFormActionLabel()}
        // on add extension flow, or enable core module flow we should always show the save bar
        shouldShowPageAction={moduleId === 'new' || editingModule.disabled}
        handleCancel={handleCancel}
      />
    </ModuleContainer>
  );
};

export default SetupModulePage;
